import React, { useState, useEffect } from 'react';
import './Leaderboard.css';

// Public leaderboard view. Consumes GET /api/leaderboard (no auth required).
// Companion to server/ratings.js which now updates ELO for both AI and
// human-vs-human games. Users below rank 1500 (default starting rating)
// show as gray; top-10 get medals.

const Leaderboard = ({ onBack, currentUserId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const { API_BASE_URL } = await import('../utils/apiBase');
        const res = await fetch(`${API_BASE_URL}/leaderboard?limit=${limit}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (!cancelled) setEntries(j.entries || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  const medal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  return (
    <div className="lb-container">
      <div className="lb-header">
        <button onClick={onBack} className="lb-back">← Back</button>
        <h1>🏆 Leaderboard</h1>
        <div className="lb-limit">
          <button className={limit === 20 ? 'active' : ''} onClick={() => setLimit(20)}>Top 20</button>
          <button className={limit === 50 ? 'active' : ''} onClick={() => setLimit(50)}>Top 50</button>
          <button className={limit === 100 ? 'active' : ''} onClick={() => setLimit(100)}>Top 100</button>
        </div>
      </div>

      {loading && <div className="lb-loading">Loading…</div>}
      {error && <div className="lb-error">Error: {error}</div>}
      {!loading && !error && entries.length === 0 && (
        <div className="lb-empty">No rated players yet. Finish a game to appear here!</div>
      )}

      {!loading && !error && entries.length > 0 && (
        <table className="lb-table">
          <thead>
            <tr>
              <th className="lb-col-rank">Rank</th>
              <th className="lb-col-name">Player</th>
              <th className="lb-col-rating">Rating</th>
              <th className="lb-col-games">Games</th>
              <th className="lb-col-winrate">Win %</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr
                key={e.id}
                className={[
                  'lb-row',
                  e.rank <= 3 && 'lb-medal',
                  e.id === currentUserId && 'lb-me',
                  e.rating < 1200 && 'lb-below-default',
                ].filter(Boolean).join(' ')}
              >
                <td className="lb-col-rank">
                  {medal(e.rank) || `#${e.rank}`}
                </td>
                <td className="lb-col-name">
                  {e.name}
                  {e.id === currentUserId && <span className="lb-you"> (you)</span>}
                </td>
                <td className="lb-col-rating">{e.rating}</td>
                <td className="lb-col-games">{e.gamesPlayed}</td>
                <td className="lb-col-winrate">{e.winRate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Leaderboard;
